import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import DeleteIcon from "@/assets/images/delete-white.svg";
import CommonImg from "@/components/Common/Img";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteProjectGroups } from "@/libs/react-query/hooks/useProjectQuery";
import { useEffect, useMemo, useState } from "react";
import { showToast } from "@/utils/toastUtils";
import { useReadProjectGroupsScenarios } from "@/libs/react-query/hooks/useScenarioQuery";
import { useReadScenarioApprovalsById } from "@/libs/react-query/hooks/useScenarioApprovalsQuery";
import { ScenarioDecision } from "@/consts/Scenario";

export default function DeleteProjectAlertDialog() {
  const { group_id = "" } = useParams();
  const [open, setOpen] = useState(false);
  const [approvalId, setApprovalId] = useState<string | null>(null);

  const navigate = useNavigate();

  const { data: scenariosData } = useReadProjectGroupsScenarios(group_id);
  const { data: approvalData, isLoading: approvalLoading } =
    useReadScenarioApprovalsById(approvalId ?? "");
  const { mutate } = useDeleteProjectGroups(group_id);

  const { scenarios = [] } = scenariosData || {};

  useEffect(() => {
    if (scenarios.length === 0) return;

    const found = scenarios.find((scenario) => !!scenario.approval_id);
    if (found?.approval_id) {
      setApprovalId(found.approval_id);
    }
  }, [scenarios]);

  const isApprovalDecided = useMemo(() => {
    if (!approvalData) return false;

    return (
      approvalData.final_decision === ScenarioDecision.APPROVED ||
      approvalData.final_decision === ScenarioDecision.REJECTED
    );
  }, [approvalData]);

  const handleDeleteProject = () => {
    if (group_id) {
      mutate(undefined, {
        onSuccess: () => {
          showToast({
            message: "프로젝트가 삭제되었습니다.",
            type: "success",
          });
          navigate("/management/projects", { replace: true });
        },
        onSettled: () => {
          setOpen(false);
        },
      });
    }
  };

  if (approvalLoading) {
    return null;
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="trigger"
          className="bg-pink hover:bg-pink/80"
          disabled={isApprovalDecided}
        >
          <CommonImg src={DeleteIcon} alt="delete" />
          프로젝트 삭제
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="h-[222px] flex flex-col">
        <div className="flex-1 flex flex-col text-center">
          <AlertDialogTitle>프로젝트를 삭제하시겠어요?</AlertDialogTitle>
          <AlertDialogDescription className="mt-8">
            프로젝트를 삭제하면, 프로젝트에 포함된 모든 정보가 함께 삭제됩니다.
          </AlertDialogDescription>
        </div>
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button
              onClick={handleDeleteProject}
              className="px-5 py-2 text-sm font-semibold bg-pink hover:bg-pink/80 rounded-md"
            >
              삭제하기
            </Button>
          </AlertDialogAction>
          <AlertDialogCancel asChild>
            <Button className="px-5 py-2 text-sm text-gray-A font-semibold bg-gray-E hover:bg-gray-E/80 rounded-md">
              취소하기
            </Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
